<template>
  <cst-page-template class="configuration-investigation-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page">
      <div class="ds-page__header">
        <h1>Configuratie onderzoeken</h1>
      </div>
      <p class="ds-page__intro">
        Voer een configuratiecode in om in detail te zien wat er gebeurd bij het ophalen van de
        configuratie. Het onderzoek doorloopt alle stappen, benoemt de vermoedelijke oorzaak en
        toont het volledige call-log.
      </p>

      <div class="ds-card">
        <div class="ds-card__header">Onderzoek uitvoeren</div>
        <div class="ds-card__body">
          <form class="configuration-investigation-page__query" @submit.prevent="run">
            <div class="ds-form__field">
              <label for="investigation-code">Configuratiecode *</label>
              <!-- :value + @input rather than v-model: the field normalises what
                   it is given, and a rejected character has to disappear from
                   the input even when the normalised value did not change. -->
              <input
                id="investigation-code"
                :value="configurationCode"
                type="text"
                maxlength="12"
                autocomplete="off"
                placeholder="VS8DH5V9"
                class="ds-field configuration-investigation-page__code"
                data-qa="investigation-code-input"
                @input="onCodeInput"
              />
            </div>

            <div class="ds-form__field">
              <label for="investigation-delivery-date">
                Leverdatum
                <span
                  class="ds-form__info"
                  title="Leverdatum - bepaalt de prijspeildatum voor prijzen. Leeg = vandaag"
                  >&#9432;</span
                >
              </label>
              <input
                id="investigation-delivery-date"
                v-model="deliveryDate"
                type="date"
                class="ds-field"
                data-qa="investigation-delivery-date-input"
              />
            </div>

            <ds-button type="submit" :disabled="busy" data-qa="investigation-run-button">
              <ds-spinner v-if="busy" inline />
              <ds-icon v-else name="search" />
              {{ busy ? 'Bezig…' : 'Onderzoek starten' }}
            </ds-button>
          </form>

          <div
            v-if="validationError"
            class="ds-alert ds-alert--warning configuration-investigation-page__validation"
          >
            <strong>{{ validationError }}</strong>
          </div>
        </div>
      </div>

      <div class="configuration-investigation-page__results">
        <div v-if="busy" class="configuration-investigation-page__loading">
          <span class="ds-spinner"></span>
          Onderzoek loopt — stappen worden doorlopen en alle API-calls vastgelegd…
        </div>

        <div v-else-if="notFoundCode" class="ds-alert ds-alert--warning">
          <span>
            <strong>Geen configuratie gevonden voor {{ notFoundCode }}.</strong>
            De code bestaat niet (meer) of hoort bij een ander merk. Controleer de code en probeer
            het opnieuw.
          </span>
        </div>

        <div v-else-if="loadError" class="ds-alert ds-alert--danger">
          <span>
            <strong>Het onderzoek is niet uitgevoerd.</strong>
            {{ loadError }}
          </span>
        </div>

        <div v-else-if="!result" class="ds-empty configuration-investigation-page__empty">
          <h2 class="ds-empty__title">Nog geen onderzoek uitgevoerd</h2>
          <p class="ds-empty__message">
            Vul een configuratiecode in en start het onderzoek. Laat de leverdatum leeg om vandaag
            als prijspeildatum te gebruiken.
          </p>
        </div>

        <template v-else>
          <!-- Verdict -->
          <div
            class="configuration-investigation-page__verdict"
            :class="`configuration-investigation-page__verdict--${succeeded ? 'ok' : 'fail'}`"
            data-qa="investigation-verdict"
          >
            <span class="configuration-investigation-page__verdict-stripe"></span>
            <div class="configuration-investigation-page__verdict-body">
              <div class="configuration-investigation-page__verdict-top">
                <span class="configuration-investigation-page__verdict-code">
                  {{ result.configurationCode }}
                </span>
                <span v-if="succeeded" class="ds-chip ds-chip--dot ds-chip--positive"
                  >Succesvol</span
                >
                <span v-if="errorCount" class="ds-chip ds-chip--dot ds-chip--danger">
                  {{ errorCount }} {{ errorCount === 1 ? 'fout' : 'fouten' }}
                </span>
                <span v-if="warningCount" class="ds-chip ds-chip--dot ds-chip--warning">
                  {{ warningCount }} waarschuwing{{ warningCount === 1 ? '' : 'en' }}
                </span>
                <span v-if="infoCount" class="ds-chip ds-chip--dot ds-chip--info">
                  {{ infoCount }} × ter info
                </span>
              </div>
              <p class="configuration-investigation-page__verdict-message">
                {{
                  succeeded
                    ? 'Alle stappen zijn zonder fouten doorlopen — deze configuratiecode levert een complete, geprijsde configuratie op.'
                    : 'Er zijn problemen gevonden bij deze configuratie.'
                }}
              </p>
              <div class="configuration-investigation-page__meta">
                <div
                  v-for="item in verdictMeta"
                  :key="item.label"
                  class="configuration-investigation-page__meta-item"
                >
                  <span class="configuration-investigation-page__meta-key">{{ item.label }}</span>
                  <span
                    class="configuration-investigation-page__meta-value"
                    :class="{ 'configuration-investigation-page__meta-value--mono': item.mono }"
                  >
                    {{ item.value }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Bevindingen -->
          <div v-if="findings.length" class="ds-card configuration-investigation-page__findings">
            <div class="ds-card__header">Bevindingen ({{ findings.length }})</div>
            <div class="ds-card__body">
              <div
                v-for="(finding, index) in findings"
                :key="index"
                class="configuration-investigation-page__finding"
                :class="`configuration-investigation-page__finding--${severityOf(
                  finding
                ).toLowerCase()}`"
              >
                <div class="configuration-investigation-page__finding-head">
                  <span class="ds-chip ds-chip--dot" :class="severityChip(finding)">
                    {{ severityLabel(finding) }}
                  </span>
                  <span class="configuration-investigation-page__finding-code">
                    {{ finding.code }}
                  </span>
                  <span class="ds-chip ds-chip--neutral">{{ formatStage(finding.stage) }}</span>
                  <span v-if="finding.system" class="ds-chip ds-chip--brand">
                    {{ finding.system }}
                  </span>
                </div>
                <p class="configuration-investigation-page__finding-message">
                  {{ finding.message }}
                </p>
                <div v-if="finding.detail" class="configuration-investigation-page__finding-detail">
                  {{ finding.detail }}
                </div>
                <div
                  v-if="finding.likelyCause"
                  class="configuration-investigation-page__finding-cause"
                >
                  <b>Vermoedelijke oorzaak</b>{{ finding.likelyCause }}
                </div>
                <div
                  v-if="finding.relatedCallIds && finding.relatedCallIds.length"
                  class="configuration-investigation-page__finding-related"
                >
                  <span>Gerelateerde calls:</span>
                  <button
                    v-for="id in finding.relatedCallIds"
                    :key="id"
                    type="button"
                    class="ds-button ds-button--ghost ds-button--sm"
                    :data-qa="`investigation-related-call-${id}`"
                    @click="jumpToCall(id)"
                  >
                    #{{ id }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="configuration-investigation-page__split">
            <!-- Stappen -->
            <div class="ds-card">
              <div class="ds-card__header">Stappen</div>
              <div class="configuration-investigation-page__stages">
                <button
                  v-for="(stage, index) in stages"
                  :key="index"
                  type="button"
                  class="configuration-investigation-page__stage"
                  :class="stageClass(stage)"
                  :title="stageTitle(stage)"
                  :data-qa="`investigation-stage-${stage.stage}`"
                  @click="pickStage(stage.stage)"
                >
                  <span class="configuration-investigation-page__stage-dot"><i></i></span>
                  <span class="configuration-investigation-page__stage-name">
                    {{ stage.stage }}
                    <span
                      v-if="stage.message"
                      class="configuration-investigation-page__stage-note"
                      >{{ stage.message }}</span
                    >
                  </span>
                  <span class="configuration-investigation-page__stage-calls">
                    {{ stageCallLabel(stage) }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Samenvatting / API-calls -->
            <div>
              <div class="ds-tabs" role="tablist">
                <button
                  v-for="tab in tabs"
                  :key="tab.key"
                  type="button"
                  class="ds-tabs__tab"
                  :class="{ 'ds-tabs__tab--active': activeTab === tab.key }"
                  role="tab"
                  :aria-selected="activeTab === tab.key"
                  data-qa="tab-header-item"
                  @click="activeTab = tab.key"
                >
                  {{ tab.label }}
                  <span
                    v-if="tab.key === 'calls'"
                    class="configuration-investigation-page__tab-count"
                  >
                    {{ callCountLabel }}
                  </span>
                </button>
              </div>

              <investigation-summary v-if="activeTab === 'summary'" :result="result" />
              <investigation-call-log
                v-else
                v-model:stage-filter="stageFilter"
                v-model:open-call-ids="openCallIds"
                v-model:only-failed="onlyFailed"
                :calls="result.calls || []"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'
import configurationInvestigationService from '@/services/configurationInvestigationService'
import InvestigationSummary from './InvestigationSummary.vue'
import InvestigationCallLog from './InvestigationCallLog.vue'
import {
  formatDate,
  formatDuration,
  formatStage,
  formatTimestamp,
  isFailedCall,
} from './investigationFormat'

// Beheer ▸ Configuratie onderzoeken — the UI for the investigate_configuration
// endpoint:
//
//   GET /api/cst/configurations/{configurationCode}/investigate
//       ?deliveryDate={yyyy-mm-dd}
//
// The form is exactly the endpoint's parameters; the result is its verdict,
// findings, stage run, summaries and the full API-call log.

const TABS = [
  { key: 'summary', label: 'Samenvatting' },
  { key: 'calls', label: 'API-calls' },
]

// A configuration code is eight characters; the input allows a couple more so a
// pasted code with a suffix is not silently cut off before it can be rejected.
const MIN_CODE_LENGTH = 8

export default {
  name: 'ConfigurationInvestigationPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    DsButton,
    DsIcon,
    DsSpinner,
    InvestigationSummary,
    InvestigationCallLog,
  },
  data() {
    return {
      tabs: TABS,
      configurationCode: '',
      deliveryDate: '',
      busy: false,
      result: null,
      notFoundCode: null,
      loadError: null,
      validationError: null,
      activeTab: 'summary',
      stageFilter: null,
      onlyFailed: false,
      openCallIds: {},
    }
  },
  computed: {
    findings() {
      return this.result?.findings || []
    },
    stages() {
      return this.result?.stages || []
    },
    succeeded() {
      return this.result?.overallStatus === 'OK'
    },
    errorCount() {
      return this.countBySeverity('ERROR')
    },
    // Counted, not derived from the total: a finding can also be INFO, and calling one of those a
    // warning made a clean configuration report "Succesvol" and "1 waarschuwing" at the same time.
    warningCount() {
      return this.countBySeverity('WARNING')
    },
    infoCount() {
      return this.countBySeverity('INFO')
    },
    failedCallCount() {
      return (this.result?.calls || []).filter(isFailedCall).length
    },
    callCountLabel() {
      const total = (this.result?.calls || []).length
      return this.failedCallCount ? `${total} · ${this.failedCallCount} fout` : `${total}`
    },
    // How many calls each stage produced, so the rail can say what clicking it
    // will show.
    callsPerStage() {
      return (this.result?.calls || []).reduce((counts, call) => {
        counts[call.stage] = (counts[call.stage] || 0) + 1
        return counts
      }, {})
    },
    verdictMeta() {
      return [
        { label: 'Onderzocht op', value: formatTimestamp(this.result.investigatedAt) },
        { label: 'Totale duur', value: formatDuration(this.result.durationMs) },
        { label: 'Product data key', value: this.result.productDataKey, mono: true },
        { label: 'Leverdatum', value: formatDate(this.result.calculationDate) },
        { label: 'API-calls', value: (this.result.calls || []).length },
      ]
    },
  },
  methods: {
    formatStage,
    countBySeverity(severity) {
      return this.findings.filter((finding) => this.severityOf(finding) === severity).length
    },
    // A finding the backend did not classify is treated as a warning, which is what it used to be
    // before INFO existed.
    severityOf(finding) {
      return finding.severity === 'ERROR' || finding.severity === 'INFO'
        ? finding.severity
        : 'WARNING'
    },
    severityChip(finding) {
      return {
        ERROR: 'ds-chip--danger',
        INFO: 'ds-chip--info',
        WARNING: 'ds-chip--warning',
      }[this.severityOf(finding)]
    },
    severityLabel(finding) {
      return { ERROR: 'Fout', INFO: 'Ter info', WARNING: 'Waarschuwing' }[this.severityOf(finding)]
    },
    onCodeInput(event) {
      const normalised = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      // Written back to the element as well: when the normalised value is
      // unchanged there is no re-render to put the rejected character right.
      event.target.value = normalised
      this.configurationCode = normalised
    },
    async run() {
      const code = this.configurationCode.trim().toUpperCase()
      if (code.length < MIN_CODE_LENGTH) {
        this.validationError = 'Vul een geldige configuratiecode in (8 tekens).'
        return
      }

      this.configurationCode = code
      this.validationError = null
      this.busy = true
      this.result = null
      this.notFoundCode = null
      this.loadError = null
      this.stageFilter = null
      this.onlyFailed = false
      this.openCallIds = {}
      this.activeTab = 'summary'

      try {
        const response = await configurationInvestigationService.investigate(code, {
          deliveryDate: this.deliveryDate || null,
        })
        this.result = response.data
      } catch (error) {
        if (error.response?.status === 404) {
          this.notFoundCode = code
        } else {
          console.error('Failed to investigate configuration:', error)
          this.loadError = 'Er is iets misgegaan bij het opvragen. Probeer het opnieuw.'
        }
      } finally {
        this.busy = false
      }
    },
    stageClass(stage) {
      return {
        OK: null,
        FAILED: 'configuration-investigation-page__stage--failed',
        WARNING: 'configuration-investigation-page__stage--warning',
        SKIPPED: 'configuration-investigation-page__stage--skipped',
      }[stage.status]
    },
    stageCallLabel(stage) {
      const count = this.callsPerStage[stage.stage] || 0
      if (!count) return '—'
      return `${count} call${count === 1 ? '' : 's'}`
    },
    stageTitle(stage) {
      const count = this.callsPerStage[stage.stage] || 0
      return count ? `Bekijk de ${count} API-call(s) van deze stap` : 'Geen API-calls in deze stap'
    },
    pickStage(stage) {
      this.activeTab = 'calls'
      this.stageFilter = stage
    },
    jumpToCall(id) {
      this.activeTab = 'calls'
      // Both filters go: either of them can hide the call this is opening.
      this.stageFilter = null
      this.onlyFailed = false
      this.openCallIds = { ...this.openCallIds, [id]: true }
      this.$nextTick(() => {
        document
          .getElementById(`investigation-call-${id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The page chrome, cards, chips, tabs, alerts, fields and toggles
// all come from the design system (styles/ds/). See styles/ds/README.md before
// adding anything here.

.configuration-investigation-page {
  &__query {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) 190px auto;
    gap: var(--ds-space-4);
    align-items: end;

    @media (max-width: 900px) {
      grid-template-columns: 1fr 1fr;

      .ds-button {
        grid-column: 1 / -1;
      }
    }
  }

  // A configuration code is read character by character, so it gets the
  // treatment every other code on the page has.
  &__code {
    font-family: $font-family-mono;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  &__validation {
    margin-top: var(--ds-space-4);
  }

  &__results {
    margin-top: var(--ds-space-5);
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 26px;
    border: 1px solid var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface);
    box-shadow: var(--ds-shadow-sm);
    font-size: var(--ds-font-size-md);
    color: var(--ds-color-text-muted);
  }

  &__empty {
    border: 1px dashed var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface);
    padding-block: 64px;
  }

  // ---- verdict ----
  &__verdict {
    display: flex;
    align-items: stretch;
    overflow: hidden;
    margin-bottom: 14px;
    border: 1px solid var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface);
    box-shadow: var(--ds-shadow-sm);
  }

  &__verdict-stripe {
    flex: 0 0 6px;
  }

  &__verdict--ok &__verdict-stripe {
    background: var(--ds-color-accent);
  }

  &__verdict--fail &__verdict-stripe {
    background: var(--ds-color-danger-border);
  }

  &__verdict-body {
    flex: 1;
    min-width: 0;
    padding: var(--ds-space-5) var(--ds-space-6);
  }

  &__verdict-top {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  &__verdict-code {
    font-family: $font-family-mono;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: var(--ds-color-brand);
  }

  &__verdict-message {
    max-width: 70ch;
    margin: var(--ds-space-2) 0 0;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--ds-color-text-muted);
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 2px var(--ds-space-5);
    margin-top: 18px;
    padding-top: var(--ds-space-4);
    border-top: 1px solid var(--ds-color-border-subtle);
  }

  &__meta-item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: var(--ds-space-1) 0;
  }

  &__meta-key {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ds-color-text-subtle);
  }

  &__meta-value {
    font-size: var(--ds-font-size-base);
    font-variant-numeric: tabular-nums;
    color: var(--ds-color-text);

    &--mono {
      font-family: $font-family-mono;
      font-size: 12.5px;
    }
  }

  // ---- findings ----
  // The chips on this page sit beside a label rather than inside a line of text, so they take a
  // pill shape: one height, a gap for the dot, a code set as a filled token that may wrap. Scoped
  // here rather than changed in ds/_chip.scss, which Addenda, Reports and the dealer accessories
  // grid already render with.
  .ds-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 28px;
    padding: 0 var(--ds-space-3);
    font-size: var(--ds-font-size-sm);
    letter-spacing: normal;

    // A code is a token, not a state: a filled box on the label's baseline, which lets a code too
    // long for its column wrap instead of widening the row until the card cuts it off.
    &--code {
      display: inline;
      height: auto;
      padding: 3px 9px;
      border: 0;
      border-radius: 6px;
      background: var(--ds-color-border-subtle);
      color: var(--ds-color-text-muted);
      font-size: var(--ds-font-size-xs);
      letter-spacing: normal;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }

  &__findings {
    margin-bottom: 14px;
  }

  &__finding {
    padding: var(--ds-space-4) 18px;
    border: 1px solid var(--ds-color-border);
    border-left: 4px solid var(--ds-color-text-subtle);
    border-radius: 10px;
    background: var(--ds-color-surface);

    + .configuration-investigation-page__finding {
      margin-top: 10px;
    }

    &--error {
      border-left-color: var(--ds-color-danger-border);
      background: var(--ds-color-danger-bg);
    }

    &--warning {
      border-left-color: var(--ds-color-warning-border);
      background: var(--ds-color-warning-bg);
    }

    // INFO is an observation, not a problem: it keeps the plain surface and only takes a tinted
    // rule, so it never reads as something that has to be acted on.
    &--info {
      border-left-color: var(--ds-color-brand);
    }
  }

  &__finding-head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__finding-code {
    font-family: $font-family-mono;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ds-color-text);
  }

  &__finding-message {
    margin: 10px 0 0;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--ds-color-text);
  }

  &__finding-detail {
    margin-top: 10px;
    padding: 10px var(--ds-space-3);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface-sunken);
    font-family: $font-family-mono;
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--ds-color-text-muted);
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__finding-cause {
    margin-top: var(--ds-space-3);
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--ds-color-text-muted);

    b {
      display: block;
      margin-bottom: 3px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ds-color-brand);
    }
  }

  &__finding-related {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: var(--ds-space-3);
    font-size: 12.5px;
    color: var(--ds-color-text-subtle);
  }

  // ---- stage rail + tab panel ----
  &__split {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    gap: 14px;
    align-items: start;

    @media (max-width: 1080px) {
      grid-template-columns: 1fr;
    }
  }

  &__stages {
    display: flex;
    flex-direction: column;
    padding: var(--ds-space-3) 0 var(--ds-space-4);
  }

  &__stage {
    position: relative;
    display: grid;
    grid-template-columns: 26px 1fr auto;
    gap: 14px;
    align-items: start;
    width: 100%;
    padding: 9px 22px;
    border: 0;
    background: none;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: var(--ds-color-surface-hover);
    }
  }

  &__stage-dot {
    position: relative;
    display: grid;
    place-items: center;
    height: 22px;

    // The connecting line between two steps; the last step has nothing to
    // connect to.
    &::before {
      content: '';
      position: absolute;
      top: 20px;
      bottom: -22px;
      width: 2px;
      background: var(--ds-color-border);
    }

    i {
      z-index: 1;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: var(--ds-color-accent);
      box-shadow: 0 0 0 3px var(--ds-color-accent-subtle);
    }
  }

  &__stage:last-child &__stage-dot::before {
    display: none;
  }

  &__stage--failed &__stage-dot i {
    background: var(--ds-color-danger-border);
    box-shadow: 0 0 0 3px var(--ds-color-danger-bg);
  }

  // A stage that warns did run and did produce something; only a skipped one stays grey.
  &__stage--warning &__stage-dot i {
    background: var(--ds-color-warning-border);
    box-shadow: 0 0 0 3px var(--ds-color-warning-bg);
  }

  &__stage--skipped &__stage-dot i {
    background: var(--ds-color-text-subtle);
    box-shadow: 0 0 0 3px var(--ds-color-border-subtle);
  }

  &__stage-name {
    font-family: $font-family-mono;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ds-color-text);
  }

  &__stage--failed &__stage-name {
    color: var(--ds-color-danger-text);
  }

  &__stage-note {
    display: block;
    margin-top: 3px;
    font-family: var(--ds-font-family);
    font-size: var(--ds-font-size-sm);
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.45;
    color: var(--ds-color-text-muted);
  }

  &__stage-calls {
    padding-top: 2px;
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
    color: var(--ds-color-text-subtle);
    white-space: nowrap;
  }

  &__tab-count {
    margin-left: 6px;
    font-size: 11.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--ds-color-text-subtle);
  }
}
</style>
